import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, message, Modal, Space, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { slugify } from 'utils/slugify';
import { globalSettingService } from '../services/globalSettingService';
import { CountryInterface } from '../types/globalSettingType';

const CountryOfOriginSection: React.FC = () => {
  const [countries, setCountries] = useState<CountryInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingCountry, setEditingCountry] = useState<CountryInterface | null>(null);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await globalSettingService.fetchCountries();
      setCountries(response.results);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      message.error('Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCountry(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (country: CountryInterface) => {
    setEditingCountry(country);
    form.setFieldsValue(country);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await globalSettingService.deleteCountry(id);
      message.success('Country deleted successfully');
      await fetchCountries();
    } catch (error: any) {
      console.error('Failed to delete country:', error);
      message.error(error?.response?.data?.detail || 'Failed to delete country');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingCountry) {
        await globalSettingService.updateCountry(editingCountry.id, {
          ...values,
          slug: slugify(values.name),
        });
        message.success('Country updated successfully');
      } else {
        await globalSettingService.createCountry({
          ...values,
          slug: slugify(values.name),
        });
        message.success('Country created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      await fetchCountries();
    } catch (error: any) {
      console.error('Failed to save country:', error);
      message.error(error?.response?.data?.detail || 'Failed to save country');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'ISO 3166-1',
      dataIndex: 'iso_code',
      key: 'iso_code',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: CountryInterface) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: '32px' }}>
      <Card
        title="Country of Origin"
        extra={
          <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
            Used for content origin.
          </div>
        }
        style={{ border: '2px solid #d9d9d9' }}
        headStyle={{ borderBottom: '2px solid #d9d9d9' }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Country of Origin
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={countries}
          loading={loading}
          rowKey="id"
          pagination={false}
        />

        <Modal
          title={editingCountry ? 'Edit Country' : 'Add Country'}
          open={isModalVisible}
          onOk={handleSubmit}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
          }}
          confirmLoading={loading}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please enter country name' }]}
            >
              <Input placeholder="e.g., Korea" />
            </Form.Item>
            <Form.Item
              name="iso_code"
              label="ISO 3166-1"
              rules={[{ required: true, message: 'Please enter ISO code' }]}
              normalize={value => value?.toUpperCase()}
            >
              <Input placeholder="e.g., KR" maxLength={2} style={{ textTransform: 'uppercase' }} />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default CountryOfOriginSection;
